import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAll';
import { loadPeopleCounts, fetchAllIn, COUNT_TIPS } from '../lib/adminCounts';
import InfoTip from '../components/InfoTip';
import { useTabParam } from '../../hooks/useTabParam';
import { currentlyReadingOf } from '../../lib/currentlyReading';
import { logAudit } from '../lib/auditLog';
function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  if (d < 30)  return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function Avatar({ user, size = 48 }) {
  const initials = ((user.username || user.display_name || 'U').slice(0, 2)).toUpperCase();
  const palettes = [
    { bg: '#fef3c7', color: 'var(--feedback-warning-ink)' }, { bg: '#d1fae5', color: '#065f46' },
    { bg: '#dbeafe', color: '#1e40af' }, { bg: '#f3e8ff', color: '#6b21a8' },
    { bg: '#fee2e2', color: '#991b1b' }, { bg: '#e0f2fe', color: '#0369a1' },
    { bg: '#fce7f3', color: '#9d174d' }, { bg: '#ecfdf5', color: '#065f46' },
  ];
  const idx = (user.username || 'a').charCodeAt(0) % palettes.length;
  const { bg, color } = palettes[idx];
  return (
    <div style={{
      width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
      background: user.avatar_url ? `url(${user.avatar_url}) center/cover` : bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.3), fontWeight: 700, color,
      overflow: 'hidden', fontFamily: 'var(--font-body)',
    }}>
      {!user.avatar_url && initials}
    </div>
  );
}

function StatusPill({ user }) {
  const style = (bg, color, border) => ({
    fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '4px 12px',
    borderRadius: 'var(--radius-xxl)', background: bg, color, border: `1px solid ${border}`,
    whiteSpace: 'nowrap', flexShrink: 0,
  });
  if (user.suspended)        return <span style={style('#fee2e2','#991b1b','#fca5a5')}>suspended</span>;
  if (user.is_admin)         return <span style={style('#292524','#fef3c7','#292524')}>admin</span>;
  if (user.report_count > 0) return <span style={style('#fee2e2','#991b1b','#fca5a5')}>flagged</span>;
  if (user.is_new)           return <span style={style('#fef3c7','#92400e','#fde68a')}>new</span>;
  return                            <span style={style('#d1fae5','#065f46','#a7f3d0')}>active</span>;
}


// ── UserDetailModal ────────────────────────────────────────────────────────────
function UserDetailModal({ user, onClose }) {
  const [profile,  setProfile]  = useState(null);
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    async function load() {
      const [{ data: prof }, auditRes] = await Promise.all([
        supabase.from('user_profiles')
          .select('bio, ask_me_about, showcase_items, currently_reading, recommend_me, to_know_me, top_tropes, archetype_label, archetype_emoji, location, instagram_handle, substack_url, suspended, suspended_at, suspend_reason')
          .eq('user_id', user.user_id)
          .single(),
        supabase.from('admin_audit_log')
          .select('action_type, entity_type, description, created_at')
          .eq('entity_id', user.user_id)
          .order('created_at', { ascending: false })
          .limit(20),
      ]);
      setProfile(prof || null);
      setHistory(auditRes.data || []);
      setLoading(false);
    }
    load();
  }, [user.user_id]);

  const Sec = ({ title, children }) => (
    <div style={{ marginBottom: 18 }}>
      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--neutral-400)', margin: '0 0 8px' }}>{title}</p>
      {children}
    </div>
  );

  const BlockPill = ({ label, active }) => (
    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '4px 10px', borderRadius: 'var(--radius-md)',
      background: active ? 'var(--feedback-success-bg)' : 'var(--neutral-100)', color: active ? '#16a34a' : 'var(--neutral-400)',
      border: `1px solid ${active ? '#a7f3d0' : '#e8e4dc'}` }}>
      {active ? '✓' : '—'} {label}
    </span>
  );

  const currentlyReading = currentlyReadingOf(profile);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-lg)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brand-white)', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--brand-cream)', display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexShrink: 0 }}>
          <Avatar user={user} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>@{user.username}</p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '2px 0 0' }}>{user.display_name} · joined {timeAgo(user.created_at)}</p>
          </div>
          <a href={`https://www.reanloforreaders.com/u/${user.username}`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: '7px 14px', borderRadius: 9, background: 'var(--neutral-100)', color: 'var(--neutral-800)', textDecoration: 'none', border: '1px solid var(--brand-sand)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Public profile ↗
          </a>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 'var(--radius-circle)', background: 'var(--neutral-100)', border: 'none', fontSize: 'var(--font-size-lg)', color: 'var(--neutral-500)', cursor: 'pointer', flexShrink: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', padding: '20px 0' }}>Loading...</p>
          ) : <>

            <Sec title="Profile Blocks">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                <BlockPill label="Ask Me About"      active={!!(profile?.ask_me_about?.services?.length)} />
                <BlockPill label="Currently Reading" active={currentlyReading.length > 0} />
                <BlockPill label="Recommend Me"      active={!!(profile?.recommend_me?.length)} />
                <BlockPill label="To Know Me"        active={!!(profile?.to_know_me?.length)} />
                <BlockPill label="Showcase"          active={!!(profile?.showcase_items?.length)} />
                <BlockPill label="Top Tropes"        active={!!(profile?.top_tropes?.length)} />
                <BlockPill label="Bio"               active={!!profile?.bio} />
                <BlockPill label="Instagram"         active={!!profile?.instagram_handle} />
                <BlockPill label="Substack"          active={!!profile?.substack_url} />
              </div>
            </Sec>

            {profile?.ask_me_about?.services?.length > 0 && (
              <Sec title="Ask Me About Services">
                <div style={{ border: '1px solid #f0e9dc', borderRadius: 'var(--radius-10)', overflow: 'hidden' }}>
                  {profile.ask_me_about.services.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', background: i % 2 ? '#fdfbf7' : 'var(--brand-white)', borderBottom: i < profile.ask_me_about.services.length - 1 ? '1px solid var(--brand-cream)' : 'none' }}>
                      <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)' }}>{s.name}</span>
                      <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--brand-rust)', fontWeight: 600 }}>{s.rate || '—'}</span>
                    </div>
                  ))}
                </div>
              </Sec>
            )}

            {/* Up to four books since the block became a list. Every one is
                listed here, not only the first the share image shows. */}
            {currentlyReading.length > 0 && (
              <Sec title={currentlyReading.length > 1 ? `Currently Reading (${currentlyReading.length})` : 'Currently Reading'}>
                {currentlyReading.map((b, i) => (
                  <p key={i} style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', margin: '0 0 5px', background: 'var(--brand-paper)', padding: '9px 14px', borderRadius: 9 }}>
                    <strong>{b.title}</strong>
                    {b.author && ` by ${b.author}`}
                  </p>
                ))}
              </Sec>
            )}

            {profile?.recommend_me?.length > 0 && (
              <Sec title={`Recommend Me (${profile.recommend_me.length})`}>
                {profile.recommend_me.slice(0, 5).map((b, i) => (
                  <div key={i} style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', padding: '7px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-md)', marginBottom: 5 }}>
                    <strong>{b.title}</strong>{b.author && ` by ${b.author}`}
                    {b.tropes?.length > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}> · {b.tropes.slice(0, 3).join(', ')}</span>}
                  </div>
                ))}
                {profile.recommend_me.length > 5 && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>+{profile.recommend_me.length - 5} more</p>}
              </Sec>
            )}

            {profile?.to_know_me?.length > 0 && (
              <Sec title={`To Know Me (${profile.to_know_me.length})`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                  {profile.to_know_me.slice(0, 6).map((b, i) => (
                    <span key={i} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 10px', borderRadius: 'var(--radius-md)', background: 'var(--brand-cream)', color: 'var(--neutral-600)' }}>{b.title}</span>
                  ))}
                  {profile.to_know_me.length > 6 && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>+{profile.to_know_me.length - 6} more</span>}
                </div>
              </Sec>
            )}

            <Sec title="Moderation History">
              {history.length === 0 ? (
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)' }}>No moderation history</p>
              ) : history.map((h, i) => (
                <div key={i} style={{ fontSize: 'var(--font-size-sm)', padding: '8px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-md)', border: '1px solid #f0e9dc', marginBottom: 5 }}>
                  <span style={{ fontWeight: 700, color: 'var(--neutral-800)' }}>{h.action_type}</span>
                  {h.description && <span style={{ color: 'var(--neutral-500)' }}>, {h.description}</span>}
                  <span style={{ color: 'var(--neutral-400)', float: 'right' }}>{timeAgo(h.created_at)}</span>
                </div>
              ))}
            </Sec>

            {profile?.suspended && (
              <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-10)' }}>
                <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--feedback-error)', margin: '0 0 4px' }}>Account Suspended</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{profile.suspend_reason || 'No reason given'} · {timeAgo(profile.suspended_at)}</p>
              </div>
            )}
          </>}
        </div>
      </div>
    </div>
  );
}

function UserRow({ user, last, onAction, working, isMobile, onView }) {
  const busy = working === user.user_id;

  const subtitleParts = [`Joined ${timeAgo(user.created_at)}`];
  if (user.book_count > 0)   subtitleParts.push(`${user.book_count} book${user.book_count !== 1 ? 's' : ''}`);
  if (user.post_count > 0)   subtitleParts.push(`${user.post_count} post${user.post_count !== 1 ? 's' : ''}`);
  if (user.report_count > 0) subtitleParts.push(`${user.report_count} report${user.report_count !== 1 ? 's' : ''} filed`);
  if (user.suspended && user.suspend_reason) subtitleParts.push(`"${user.suspend_reason}"`);

  const btnBase = {
    fontSize: isMobile ? 12 : 13, fontWeight: 600,
    padding: isMobile ? '6px 12px' : '8px 16px',
    borderRadius: 'var(--radius-10)', cursor: busy ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap', opacity: busy ? 0.5 : 1,
    fontFamily: 'var(--font-body)', transition: 'opacity 0.15s',
  };

  return (
    <div style={{
      padding: '16px 0',
      borderBottom: last ? 'none' : '1px solid var(--brand-cream)',
      background: user.suspended ? 'rgba(254,226,226,0.06)' : 'transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 10 : 16, marginBottom: isMobile ? 10 : 0 }}>
        <Avatar user={user} size={isMobile ? 38 : 48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
              @{user.username || 'unnamed'}
            </span>
            <StatusPill user={user} />
            <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', whiteSpace: 'nowrap' }}>
              📖 Reader
            </span>
            {user.has_author_account && (
              <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', whiteSpace: 'nowrap' }}>
                ✍️ Author
              </span>
            )}
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
            {subtitleParts.join(' · ')}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-14)', marginTop: 'var(--space-xs)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>
              <strong style={{ color: 'var(--neutral-600)', fontWeight: 600 }}>{(user.follower_count ?? 0).toLocaleString()}</strong> followers
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>
              <strong style={{ color: 'var(--neutral-600)', fontWeight: 600 }}>{(user.following_count ?? 0).toLocaleString()}</strong> following
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginLeft: isMobile ? 0 : 64, marginTop: isMobile ? 0 : 10 }}>
        <button onClick={onView}
          style={{ ...btnBase, border: '1.5px solid var(--brand-forest)', background: 'var(--feedback-success-bg)', color: 'var(--brand-forest)' }}>
          View details
        </button>
        {user.suspended ? (
          <button onClick={() => onAction('unsuspend', user)} disabled={busy}
            style={{ ...btnBase, border: '1.5px solid #86efac', background: 'var(--feedback-success-bg)', color: '#065f46' }}>
            Unsuspend
          </button>
        ) : (
          <button onClick={() => onAction('suspend', user)} disabled={busy}
            style={{ ...btnBase, border: '1.5px solid #fca5a5', background: '#fff8f8', color: 'var(--feedback-error)' }}>
            {busy ? '...' : 'Suspend'}
          </button>
        )}

        {user.is_admin ? (
          <button onClick={() => onAction('removeAdmin', user)} disabled={busy}
            style={{ ...btnBase, border: '1.5px solid var(--feedback-warning-border)', background: '#fffbeb', color: 'var(--feedback-warning-ink)' }}>
            Remove admin
          </button>
        ) : (
          <button onClick={() => onAction('makeAdmin', user)} disabled={busy}
            style={{ ...btnBase, border: '1.5px solid #d8b4fe', background: '#faf5ff', color: '#6b21a8' }}>
            Make admin
          </button>
        )}
      </div>
    </div>
  );
}

function StatsBar({ isMobile }) {
  const [stats, setStats] = useState({
    allPeople: 0, readerAccounts: 0, newThisWeek: 0, suspended: 0, reported: 0,
  });

  useEffect(() => {
    async function load() {
      try {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        // Every card here asks the database to count. Reading .length off the
        // loaded array instead is what pinned "Total users" to the page size
        // for days: the length was the size of the page, not of the table.
        const [people, { count: newThisWeek }, { count: suspended }] = await Promise.all([
          loadPeopleCounts(),
          supabase.from('user_profiles').select('*', { count: 'exact', head: true }).gte('created_at', sevenDaysAgo),
          supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('suspended', true),
        ]);
        let reported = 0;
        try {
          const { count } = await supabase.from('reports').select('*', { count: 'exact', head: true }).eq('entity_type', 'user').eq('status', 'open');
          reported = count || 0;
        } catch (_) {}
        setStats({
          allPeople:      people.allPeople,
          readerAccounts: people.readerAccounts,
          newThisWeek:    newThisWeek || 0,
          suspended:      suspended || 0,
          reported,
        });
      } catch (err) {
        console.error('UsersSection stats error:', err?.message || err);
      }
    }
    load();
  }, []);

  // The headline is people, not rows. It used to be the user_profiles count
  // labelled "total users", which left the 66 author-only people out of a
  // number that claimed to be everybody — and made the three role cards below
  // it sum to more than the total above them.
  const cards = [
    { label: 'All people',      value: stats.allPeople,      accent: '#2563eb', sub: 'distinct accounts',  tip: COUNT_TIPS.allPeople },
    { label: 'Reader accounts', value: stats.readerAccounts, accent: '#7c3aed', sub: 'signed up as a reader', tip: COUNT_TIPS.readerAccounts },
    { label: 'New this week',   value: stats.newThisWeek,    accent: '#16a34a', sub: 'joined last 7 days', tip: COUNT_TIPS.newThisWeek },
    { label: 'Suspended',       value: stats.suspended,      accent: '#d97706', sub: 'cannot sign in',     tip: COUNT_TIPS.suspendedUsers },
    { label: 'Open reports',    value: stats.reported,       accent: '#dc2626', sub: 'against users',      tip: COUNT_TIPS.openReports },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: isMobile ? 10 : 12, marginBottom: 22 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderTop: `3px solid ${c.accent}`, borderRadius: 12, padding: isMobile ? '12px 14px' : '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.label}</span>
            <InfoTip text={c.tip} />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>{c.value.toLocaleString()}</div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginTop: 5 }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}


function AccountTypeBar({ isMobile }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let alive = true;
    loadPeopleCounts()
      .then(c => { if (alive) setCounts(c); })
      .catch(err => console.error('AccountTypeBar load error:', err?.message || err));
    return () => { alive = false; };
  }, []);

  // These three sum to "All people" above, by construction:
  //   readerOnly + authorOnly + both = readers + authors - both = allPeople
  const cards = [
    { label: 'Reader only', value: counts?.readerOnly, accent: '#2563eb', sub: 'no author account', tip: COUNT_TIPS.readerOnly },
    { label: 'Author only', value: counts?.authorOnly, accent: '#92400e', sub: 'no reader account', tip: COUNT_TIPS.authorOnly },
    { label: 'Both',        value: counts?.both,       accent: '#7c3aed', sub: 'reader and author', tip: COUNT_TIPS.both       },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 10 : 12, marginBottom: 22 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderTop: `3px solid ${c.accent}`, borderRadius: 12, padding: isMobile ? '12px 14px' : '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.label}</span>
            <InfoTip text={c.tip} />
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>
            {c.value === undefined ? '—' : c.value.toLocaleString()}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginTop: 5 }}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

export default function UsersSection({ onBadgeChange, isMobile }) {
  const [users,    setUsers]    = useState([]);
  const [totalReaders, setTotalReaders] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [working,  setWorking]  = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [search,   setSearch]   = useState('');
  const [tab,      setTab]      = useTabParam('tab', 'all')
  const onBadgeRef = useRef(onBadgeChange);
  useEffect(() => { onBadgeRef.current = onBadgeChange; }, [onBadgeChange]);

  // These feed per-user tallies shown beside each row. A failure means the
  // tallies read low rather than the page breaking, so an empty result is the
  // right fallback — but it is logged, not swallowed, because a silent zero
  // here looks exactly like a real zero.
  const safeQuery = async (fn) => {
    try { return (await fn()) || []; }
    catch (err) { console.error('UsersSection tally query failed:', err?.message || err); return []; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The whole table, not a page. The list used to load the 500 most recent
      // while the cards counted all 864, so the tabs underneath were counting
      // a sample and the cards above were counting the table — two different
      // denominators on one screen, which is what made these numbers
      // impossible to reconcile. The select is nine narrow columns, so
      // fetching every row costs little and removes the mismatch entirely.
      const [profiles, { count: profileTotal }] = await Promise.all([
        fetchAllRows(() => supabase
          .from('user_profiles')
          .select('user_id, username, display_name, avatar_url, is_admin, suspended, suspended_at, suspend_reason, created_at'),
          { orderColumn: 'user_id' }),
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
      ]);

      // fetchAllRows pages by a unique column, so restore newest-first here.
      profiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTotalReaders(profileTotal ?? null);

      const userIds = profiles.map(p => p.user_id).filter(Boolean);
      if (!userIds.length) { setUsers([]); setLoading(false); return; }

      // Two limits bite on these at once: a long .in() list blows the URL
      // length, and the response caps at 1,000 rows. user_library across 864
      // readers is well past 1,000, so a plain .in() returned a truncated set
      // and every book count below was quietly too low. fetchAllIn chunks the
      // ids AND pages each chunk.
      const [books, posts, followerRows, followingRows, authorRows, reportRows] = await Promise.all([
        safeQuery(() => fetchAllIn('user_library',    'user_id',           'user_id',      userIds)),
        safeQuery(() => fetchAllIn('posts',           'user_id',           'user_id',      userIds)),
        safeQuery(() => fetchAllIn('follows',         'following_id',      'following_id', userIds)),
        safeQuery(() => fetchAllIn('follows',         'follower_id',       'follower_id',  userIds)),
        safeQuery(() => fetchAllIn('author_profiles', 'user_id',           'user_id',      userIds)),
        safeQuery(() => fetchAllRows(() => supabase.from('reports').select('reported_user_id').eq('entity_type', 'user').eq('status', 'open'))),
      ]);

      const bMap={}, pMap={}, frMap={}, fgMap={}, rMap={};
      books.forEach(r        => { bMap[r.user_id]       = (bMap[r.user_id]       || 0) + 1; });
      posts.forEach(r        => { pMap[r.user_id]       = (pMap[r.user_id]       || 0) + 1; });
      followerRows.forEach(r => { frMap[r.following_id] = (frMap[r.following_id] || 0) + 1; });
      followingRows.forEach(r=> { fgMap[r.follower_id]  = (fgMap[r.follower_id]  || 0) + 1; });
      reportRows.forEach(r   => { if (r.reported_user_id) rMap[r.reported_user_id] = (rMap[r.reported_user_id] || 0) + 1; });
      const authorSet = new Set(authorRows.map(r => r.user_id));

      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const enriched = profiles.map(p => ({
        ...p,
        book_count:         bMap[p.user_id]  || 0,
        post_count:         pMap[p.user_id]  || 0,
        follower_count:     frMap[p.user_id] || 0,
        following_count:    fgMap[p.user_id] || 0,
        report_count:       rMap[p.user_id]  || 0,
        is_new:             new Date(p.created_at) > sevenDaysAgo,
        has_author_account: authorSet.has(p.user_id),
      }));

      setUsers(enriched);
      const flagged = enriched.filter(u => u.report_count > 0).length;
      onBadgeRef.current?.('users', flagged);
    } catch (err) {
      console.error('UsersSection load error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAction(action, user) {
    setWorking(user.user_id);
    try {
      // Every one of these wrote the audit entry whether or not the update
      // landed. The Supabase client returns errors rather than throwing, so the
      // try/catch below never fired and the log recorded an action that had not
      // happened.
      //
      // `removeAdmin` is the sharp one: `trg_protect_admin_flag` REFUSES any
      // update clearing the admin flag on a row in `protected_admins` — which
      // is the founder's own two accounts. Pressing the button on one of those
      // returns an error, changed nothing, and wrote "Removed admin from @x"
      // into the audit log. The log then disagreed with the database, and the
      // log is what anyone reads afterwards.
      if (action === 'suspend') {
        const reason = prompt(`Reason for suspending @${user.username} (optional):`);
        if (reason === null) { setWorking(null); return; }
        const { error } = await supabase.from('user_profiles').update({ suspended: true, suspended_at: new Date().toISOString(), suspend_reason: reason || null }).eq('user_id', user.user_id);
        if (error) throw new Error(`Not suspended, nothing changed: ${error.message}`);
        await logAudit('update', 'user', `Suspended @${user.username}${reason ? `, "${reason}"` : ''}`, user.user_id);
      }
      if (action === 'unsuspend') {
        const { error } = await supabase.from('user_profiles').update({ suspended: false, suspended_at: null, suspend_reason: null }).eq('user_id', user.user_id);
        if (error) throw new Error(`Not unsuspended, nothing changed: ${error.message}`);
        await logAudit('update', 'user', `Unsuspended @${user.username}`, user.user_id);
      }
      if (action === 'makeAdmin') {
        if (!confirm(`Make @${user.username} an admin?`)) { setWorking(null); return; }
        const { error } = await supabase.from('user_profiles').update({ is_admin: true }).eq('user_id', user.user_id);
        if (error) throw new Error(`Admin not granted, nothing changed: ${error.message}`);
        await logAudit('update', 'user', `Granted admin to @${user.username}`, user.user_id);
      }
      if (action === 'removeAdmin') {
        if (!confirm(`Remove admin from @${user.username}?`)) { setWorking(null); return; }
        const { error } = await supabase.from('user_profiles').update({ is_admin: false }).eq('user_id', user.user_id);
        if (error) throw new Error(`Admin not removed, nothing changed: ${error.message}`);
        await logAudit('update', 'user', `Removed admin from @${user.username}`, user.user_id);
      }
      load();
    } catch (err) { alert('Error: ' + err.message); }
    setWorking(null);
  }

  const TABS = [
    { id: 'all',         label: 'All',            fn: () => true },
    { id: 'new',         label: 'New',            fn: u => u.is_new },
    { id: 'reader_only', label: '📖 Reader only', fn: u => !u.has_author_account },
    { id: 'both',        label: '📖✍️ Both',      fn: u => u.has_author_account },
    { id: 'flagged',     label: 'Flagged',        fn: u => u.report_count > 0 },
    { id: 'suspended',   label: 'Suspended',      fn: u => u.suspended },
    { id: 'admin',       label: 'Admins',         fn: u => u.is_admin },
  ];

  const activeTabDef = TABS.find(t => t.id === tab) || TABS[0];
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || (u.username || '').toLowerCase().includes(q) || (u.display_name || '').toLowerCase().includes(q);
    return matchSearch && activeTabDef.fn(u);
  });

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-xl)', gap: 'var(--space-md)', flexDirection: isMobile ? 'column' : 'row' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Users</h1>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 4 }}>
            {(totalReaders ?? users.length).toLocaleString()} reader accounts · manage accounts
            {totalReaders !== null && totalReaders > users.length && (
              <span style={{ color: '#d97706' }}> · only {users.length.toLocaleString()} loaded</span>
            )}
          </p>
        </div>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search username..."
          style={{ width: isMobile ? '100%' : 280, padding: '10px 16px', fontSize: 'var(--font-size-base)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', outline: 'none', fontFamily: 'var(--font-body)', color: 'var(--neutral-800)', background: 'var(--brand-white)', boxSizing: 'border-box' }}
        />
      </div>

      <StatsBar isMobile={isMobile} />
      <AccountTypeBar isMobile={isMobile} />

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xl)', padding: isMobile ? '0 14px 8px' : '0 24px 8px' }}>
        <div style={{ display: 'flex', borderBottom: '1.5px solid var(--brand-sand)', marginBottom: 'var(--space-none)', paddingTop: 'var(--space-xs)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(t => {
            // Counted off `users`, which is now every reader account rather
            // than a 500-row page — so these agree with the cards above
            // instead of quietly describing a sample. "Reader only" here and
            // "Reader only" on the card are the same number.
            const count = users.filter(t.fn).length;
            const isActive = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ fontSize: isMobile ? 12.5 : 13.5, fontWeight: isActive ? 700 : 500, padding: isMobile ? '12px 14px' : '14px 20px', border: 'none', background: 'transparent', color: isActive ? 'var(--neutral-800)' : 'var(--neutral-400)', borderBottom: isActive ? '2px solid var(--neutral-800)' : '2px solid transparent', marginBottom: -1.5, cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t.label}{count > 0 ? ` (${count.toLocaleString()})` : ''}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading users...</div>
        ) : !filtered.length ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            {search ? `No users matching "${search}"` : 'No users in this category'}
          </div>
        ) : (
          filtered.map((user, idx) => (
            <UserRow key={user.user_id} user={user} last={idx === filtered.length - 1} onAction={handleAction} working={working} isMobile={isMobile} onView={() => setViewingUser(user)} />
          ))
        )}
      </div>
      {viewingUser && <UserDetailModal user={viewingUser} onClose={() => setViewingUser(null)} />}
    </div>
  );
}